import { useQuery } from '@tanstack/react-query'
import type { AuthMeResponse } from '@ayub/shared'
import { ApiError } from '@/lib/api'
import { fetchAuthMe } from './api'

export type SessionStatus = 'loading' | 'authenticated' | 'unauthenticated' | 'error'

export interface AuthSession {
  status: SessionStatus
  me: AuthMeResponse | undefined
  refetch: () => void
}

/**
 * Sessiya holati — `GET /api/auth/me` ga tayanadi.
 *
 * `status` maydoni (ayniqsa `unauthenticated`) ASOSAN brauzer rejimi uchun mo'ljallangan
 * (`App.tsx`dagi `BrowserGate`): Telegram ichida har bir so'rov o'zi `initData` bilan
 * autentifikatsiya bo'ladi, alohida sessiya tekshiruvi shart emas. Shunga qaramay bu hook
 * Telegram rejimida ham ishlatiladi (`settings/hooks.ts`dagi `useCurrentAdmin()` orqali,
 * ATAYLAB — ikkalasi bir xil `['auth','me']` so'roviga tayanadi, faqat `me`/`refetch`
 * qiymatlari kerak, `status`ning `unauthenticated` shoxobchasi u yerda ishlatilmaydi).
 *
 * 401 (`AUTH_MISSING`/`AUTH_INVALID`/`AUTH_EXPIRED` — cookie yo'q yoki eskirgan) XATO EMAS,
 * bu "hali kirmagan" degani — `unauthenticated` qaytadi va qayta urinilmaydi (login
 * ekranidan boshqa hech narsa yordam bermaydi). Boshqa xatolar (tarmoq, 5xx) — `error`,
 * alohida "qayta urinish" ekrani kerak bo'ladi.
 */
export function useAuthSession(): AuthSession {
  const query = useQuery<AuthMeResponse, Error>({
    queryKey: ['auth', 'me'],
    queryFn: fetchAuthMe,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.statusCode === 401) return false
      return failureCount < 2
    },
  })

  // Diqqat: `query.isError` ENG BIRINCHI tekshiriladi, `query.data` mavjudligidan oldin.
  // TanStack Query v5'da fon qayta so'rovi (background refetch) muvaffaqiyatsiz bo'lsa,
  // `data` ESKI (oldingi muvaffaqiyatli) qiymatni saqlab qoladi — faqat `error`/`isError`
  // yangilanadi. Avval bu yerda avval `query.data` tekshirilgani uchun sessiya haqiqatda
  // brauzerda tugab (fon qayta so'rovi 401 qaytarib) ham `status` "authenticated" bo'lib
  // qolardi (eskirgan `data` hali "rost" ko'rinardi) — admin o'lik ekranda qotib qolardi.
  const status: SessionStatus = query.isError
    ? query.error instanceof ApiError && query.error.statusCode === 401
      ? 'unauthenticated'
      : 'error'
    : query.data
      ? 'authenticated'
      : 'loading'

  return {
    status,
    me: query.data,
    refetch: () => void query.refetch(),
  }
}
